import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, Save } from "lucide-react";
import api, { imageUrl } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { LANGUAGES } from "@/lib/i18n";

export default function AdminSettings() {
  const { reloadSettings } = useApp();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [migStatus, setMigStatus] = useState(null);
  const [migResult, setMigResult] = useState(null);
  const [migBusy, setMigBusy] = useState(false);

  const checkMigration = async () => {
    setMigBusy(true);
    try {
      const { data } = await api.get("/admin/migration/image-backfill/status");
      setMigStatus(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Status check failed");
    } finally {
      setMigBusy(false);
    }
  };

  const runMigration = async () => {
    if (!window.confirm("Run the one-time image backfill? It is idempotent and only fills empty image links.")) return;
    setMigBusy(true);
    try {
      const { data } = await api.post("/admin/migration/image-backfill");
      setMigResult(data);
      toast.success(`Done — ${data.dishes_image_id_filled} dishes linked, ${data.images_inserted} image records added`);
      await checkMigration();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Migration failed — no changes made");
    } finally {
      setMigBusy(false);
    }
  };

  const load = async () => {
    const { data } = await api.get("/settings");
    setSettings(data);
  };
  useEffect(() => {
    load();
  }, []);

  const set = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/admin/settings", {
        business_name: settings.business_name,
        whatsapp_number: settings.whatsapp_number || "",
        whatsapp_numbers: Array.isArray(settings.whatsapp_numbers) ? settings.whatsapp_numbers : [],
        contact_phone: settings.contact_phone || "",
        contact_email: settings.contact_email || "",
        address: settings.address || "",
        active_languages: settings.active_languages,
        default_language: settings.default_language,
        logo_image_id: settings.logo_image_id || null,
        terms_and_conditions: settings.terms_and_conditions || "",
        gst_enabled: settings.gst_enabled !== false,
        gst_rate: settings.gst_rate != null ? Number(settings.gst_rate) : 5,
      });
      toast.success("Settings saved");
      await reloadSettings();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/images", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set("logo_image_id", data.id);
      toast.success("Logo uploaded — remember to Save");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const toggleLang = (code) => {
    const active = new Set(settings.active_languages || []);
    if (active.has(code)) active.delete(code);
    else active.add(code);
    // Ensure at least EN stays active
    if (!active.has("en")) active.add("en");
    set("active_languages", Array.from(active));
  };

  if (!settings) return <div>Loading…</div>;

  return (
    <div className="mx-auto max-w-3xl">
      <div>
        <span className="kbd-eyebrow">Admin</span>
        <h1 className="mt-3 font-serif-display text-3xl font-semibold text-secondary">Settings</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Configure business identity, logo, WhatsApp number, and active languages.
        </p>
      </div>

      <form onSubmit={save} className="mt-8 space-y-8" data-testid="admin-settings-form">
        <section className="surface-card space-y-4">
          <h2 className="font-serif-display text-lg font-semibold text-secondary">Business identity</h2>

          <div>
            <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Business name</label>
            <input
              data-testid="admin-settings-business-name"
              value={settings.business_name || ""}
              onChange={(e) => set("business_name", e.target.value)}
              className="w-full rounded-xl border border-border/70 bg-background px-4 py-2.5"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Official logo</label>
            <div className="flex items-center gap-4">
              {settings.logo_image_id ? (
                <img
                  src={imageUrl(settings.logo_image_id)}
                  alt="Logo"
                  className="h-16 w-auto rounded-lg border border-border/70 bg-background p-2"
                  data-testid="admin-settings-logo-preview"
                />
              ) : (
                <div className="grid h-16 w-32 place-items-center rounded-lg border border-dashed border-primary/40 bg-muted text-[9px] font-semibold uppercase tracking-[0.14em] text-primary/70">
                  Upload official logo
                </div>
              )}
              <label className="btn-primary cursor-pointer !py-2 !text-xs" data-testid="admin-settings-logo-upload-label">
                <Upload className="h-3.5 w-3.5" /> {uploading ? "Uploading…" : "Upload logo"}
                <input type="file" accept="image/*" className="hidden" onChange={uploadLogo} data-testid="admin-settings-logo-upload-input" />
              </label>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Owner-supplied logo. It is used exactly as provided — never recolored or recreated.
            </p>
          </div>
        </section>

        <section className="surface-card space-y-4">
          <h2 className="font-serif-display text-lg font-semibold text-secondary">Contact & WhatsApp</h2>

          <div>
            <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              WhatsApp business number
            </label>
            <input
              data-testid="admin-settings-whatsapp"
              value={settings.whatsapp_number || ""}
              onChange={(e) => set("whatsapp_number", e.target.value)}
              placeholder="Empty by default — enter e.g. 919876543210"
              className="w-full rounded-xl border border-border/70 bg-background px-4 py-2.5"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              While both this field AND the additional numbers list below are empty, the WhatsApp button is hidden site-wide.
            </p>
          </div>

          <div>
            <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              Additional WhatsApp numbers (one per line)
            </label>
            <textarea
              data-testid="admin-settings-whatsapp-list"
              rows={3}
              value={(settings.whatsapp_numbers || []).join("\n")}
              onChange={(e) =>
                set(
                  "whatsapp_numbers",
                  e.target.value.split(/\r?\n/).map((s) => s.trim()).filter(Boolean)
                )
              }
              placeholder="+91 7738094058&#10;+91 9821797861"
              className="w-full rounded-xl border border-border/70 bg-background px-4 py-2.5 font-mono text-sm"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              When two or more numbers are set, the WhatsApp button opens a small choice popover.
              These take priority over the single field above.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Contact phone</label>
              <input
                data-testid="admin-settings-phone"
                value={settings.contact_phone || ""}
                onChange={(e) => set("contact_phone", e.target.value)}
                className="w-full rounded-xl border border-border/70 bg-background px-4 py-2.5"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Contact email</label>
              <input
                type="email"
                data-testid="admin-settings-email"
                value={settings.contact_email || ""}
                onChange={(e) => set("contact_email", e.target.value)}
                className="w-full rounded-xl border border-border/70 bg-background px-4 py-2.5"
              />
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Address</label>
            <textarea
              data-testid="admin-settings-address"
              rows={2}
              value={settings.address || ""}
              onChange={(e) => set("address", e.target.value)}
              className="w-full rounded-xl border border-border/70 bg-background px-4 py-2.5"
            />
          </div>
        </section>

        <section className="surface-card space-y-4">
          <h2 className="font-serif-display text-lg font-semibold text-secondary">Languages & currency</h2>
          <p className="text-xs text-muted-foreground">
            Currency is fixed to INR (₹) in Module 1. Interface translations are scaffolded — dish names, descriptions and business info are never auto-translated.
          </p>
          <div className="flex flex-wrap gap-2" data-testid="admin-settings-languages">
            {LANGUAGES.map((l) => {
              const active = (settings.active_languages || []).includes(l.code);
              return (
                <button
                  key={l.code}
                  type="button"
                  data-testid={`admin-settings-lang-${l.code}`}
                  onClick={() => toggleLang(l.code)}
                  className={`rounded-full border px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] ${
                    active
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border/70 bg-background text-secondary/80"
                  }`}
                >
                  {l.native} <span className="ml-1 opacity-70">{l.label}</span>
                </button>
              );
            })}
          </div>
          <div className="text-xs text-muted-foreground">
            Currency: <span className="font-semibold text-secondary">₹ INR</span> (locked in Module 1)
          </div>
        </section>

        <section className="surface-card space-y-3">
          <h2 className="font-serif-display text-lg font-semibold text-secondary">Terms &amp; Conditions</h2>
          <p className="text-xs text-muted-foreground">
            Owner-approved wording. Displayed on the public /terms page and referenced in the price summary.
          </p>
          <textarea
            data-testid="admin-settings-terms"
            rows={16}
            value={settings.terms_and_conditions || ""}
            onChange={(e) => set("terms_and_conditions", e.target.value)}
            className="w-full whitespace-pre-wrap rounded-xl border border-border/70 bg-background px-4 py-3 font-mono text-xs leading-relaxed"
          />
        </section>

        <div className="flex justify-end">
          <button type="submit" data-testid="admin-settings-save" className="btn-primary" disabled={saving}>
            <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save settings"}
          </button>
        </div>
      </form>

      <section className="surface-card mt-8 space-y-3" data-testid="admin-gst-section">
        <h2 className="font-serif-display text-lg font-semibold text-secondary">GST</h2>
        <p className="text-xs text-muted-foreground">
          Applied server-side when you price a quote (subtotal + additional charges). Already-priced and
          approved quotes keep their frozen GST — changes here only affect quotes you price afterwards.
        </p>
        <div className="flex flex-wrap items-center gap-6">
          <label className="flex items-center gap-2 text-sm font-medium text-secondary">
            <input
              type="checkbox"
              data-testid="admin-gst-enabled"
              checked={settings.gst_enabled !== false}
              onChange={(e) => set("gst_enabled", e.target.checked)}
              className="h-4 w-4 accent-primary"
            />
            GST enabled
          </label>
          <label className="flex items-center gap-2 text-sm">
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Rate (%)</span>
            <input
              type="number"
              min="0"
              max="100"
              step="0.1"
              data-testid="admin-gst-rate"
              value={settings.gst_rate != null ? settings.gst_rate : 5}
              onChange={(e) => set("gst_rate", e.target.value)}
              className="w-24 rounded-xl border border-border/70 bg-background px-3 py-1.5"
            />
          </label>
          <button type="button" data-testid="admin-gst-save" onClick={save} disabled={saving} className="btn-primary !py-2 !text-xs">
            {saving ? "Saving…" : "Save GST settings"}
          </button>
        </div>
      </section>

      <section className="surface-card mt-8 space-y-4" data-testid="admin-migration-section">
        <h2 className="font-serif-display text-lg font-semibold text-secondary">Dish image data migration</h2>
        <p className="text-xs text-muted-foreground">
          One-time backfill that links dish records to their existing images. Idempotent — it only fills
          empty image links and never touches the logo, prices, T&amp;Cs or any other data.
        </p>
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            data-testid="admin-migration-check-btn"
            onClick={checkMigration}
            disabled={migBusy}
            className="rounded-full border border-border/70 bg-background px-5 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-secondary/90 hover:border-primary/50"
          >
            {migBusy ? "Working…" : "Check status"}
          </button>
          {migStatus && migStatus.ready && !migStatus.already_done && (
            <button
              type="button"
              data-testid="admin-migration-run-btn"
              onClick={runMigration}
              disabled={migBusy}
              className="btn-primary !py-2 !text-xs"
            >
              Run image backfill
            </button>
          )}
        </div>
        {migStatus && (
          <div className="rounded-xl border border-border/70 bg-muted/40 p-4 font-mono text-xs leading-relaxed" data-testid="admin-migration-status">
            <div>Dishes: {migStatus.dishes_total} · with image link: {migStatus.dishes_with_image_id}</div>
            <div>Image records in DB: {migStatus.images_docs} · available in export: {migStatus.export_images_available}</div>
            <div>
              {migStatus.already_done
                ? "✓ Migration already complete — nothing to do."
                : migStatus.ready
                ? "Ready to run — dish count verified (138)."
                : `⚠ Blocked — expected 138 dishes, found ${migStatus.dishes_total}.`}
            </div>
          </div>
        )}
        {migResult && (
          <div className="rounded-xl border border-primary/40 bg-primary/5 p-4 font-mono text-xs leading-relaxed" data-testid="admin-migration-result">
            <div>BEFORE dishes={migResult.before.dishes_total} with_image={migResult.before.dishes_with_image_id} images={migResult.before.images_docs}</div>
            <div>Inserted image records: {migResult.images_inserted} · Dishes linked: {migResult.dishes_image_id_filled}</div>
            <div>AFTER dishes={migResult.after.dishes_total} with_image={migResult.after.dishes_with_image_id} images={migResult.after.images_docs}</div>
            <div>{migResult.invariants_ok ? "✓ Invariants intact (logo & dish count unchanged)" : "✗ INVARIANT FAILURE"}</div>
          </div>
        )}
      </section>
    </div>
  );
}
